import React, { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Dropdown,
    Option,
    Label,
    Text,
    Title3,
    makeStyles,
    shorthands,
    tokens,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
} from '@fluentui/react-components';
import { DatePicker, defaultDatePickerStrings } from '@fluentui/react-datepicker-compat';
import { ArrowSyncRegular, ArrowDownloadRegular } from '@fluentui/react-icons';
import { saveAs } from 'file-saver';
import Configuration from '../../config/einvoicing-configuration';
import { Loading } from '../../components/common/Loading';
import { ErrorState } from '../../components/common/ErrorState';
import { usePageTitle } from '../../hooks/usePageTitle';
import { acquireAccessToken, clearReauthFlag, recoverFromUnauthorized } from '../../utils/authToken';
import {
    constructExportFailedDocumentsUrl,
    constructDocumentSearchUrl,
    constructPostRequest,
    getDefaultSearchParams,
} from '../../controller/dashboardController';
import { getOptionsFromDimension } from '../dashboard/DocumentSearchFilterOptions';
import {
    parseFailedDocumentsCsv,
    buildAnalysis,
    buildDetailCsv,
    formatBinLabel,
    FailedDocumentRow,
    FailureAnalysis as Analysis,
    TimeBin,
} from './failureAnalysisUtils';
import { RootCause } from './rootCauseClassifier';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.padding('24px'),
        ...shorthands.gap('20px'),
        boxSizing: 'border-box',
        width: '100%',
    },
    filterBar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        ...shorthands.gap(tokens.spacingHorizontalL),
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalXS),
        minWidth: '160px',
    },
    kpiRow: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap(tokens.spacingHorizontalL),
    },
    kpiCard: {
        flexGrow: 1,
        minWidth: '160px',
        ...shorthands.padding('16px'),
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
        backgroundColor: tokens.colorNeutralBackground1,
        boxShadow: tokens.shadow4,
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('4px'),
    },
    kpiValue: {
        fontSize: '1.75rem',
        fontWeight: 700,
        lineHeight: '2rem',
    },
    kpiLabel: {
        fontSize: '0.75rem',
        color: tokens.colorNeutralForeground3,
    },
    panels: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap(tokens.spacingHorizontalL),
    },
    panel: {
        flexGrow: 1,
        minWidth: '360px',
        flexBasis: '360px',
        ...shorthands.padding('16px'),
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
        backgroundColor: tokens.colorNeutralBackground1,
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('12px'),
    },
    panelWide: {
        flexBasis: '100%',
    },
    barRow: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('8px'),
    },
    barLabel: {
        width: '180px',
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    barTrack: {
        flexGrow: 1,
        height: '16px',
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.borderRadius('4px'),
        overflow: 'hidden',
    },
    barFill: {
        height: '100%',
        backgroundColor: tokens.colorBrandBackground,
    },
    barStack: {
        display: 'flex',
        height: '100%',
        width: '100%',
    },
    barFillUnknown: {
        height: '100%',
        backgroundColor: tokens.colorBrandBackground,
    },
    barFillProvider: {
        height: '100%',
        backgroundColor: '#8764B8',
    },
    legend: {
        display: 'flex',
        ...shorthands.gap('16px'),
        marginBottom: '4px',
        fontSize: '0.75rem',
        color: tokens.colorNeutralForeground3,
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('6px'),
    },
    legendSwatch: {
        width: '12px',
        height: '12px',
        ...shorthands.borderRadius('2px'),
        display: 'inline-block',
    },
    barCount: {
        width: '56px',
        textAlign: 'right',
        fontSize: '0.75rem',
        fontVariantNumeric: 'tabular-nums',
    },
    downloadBtn: {
        minWidth: 'auto',
    },
    heatTable: {
        borderCollapse: 'collapse',
        width: '100%',
        fontSize: '0.7rem',
    },
    heatTh: {
        ...shorthands.padding('4px', '6px'),
        textAlign: 'left',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    heatThRotate: {
        fontSize: '0.65rem',
        maxWidth: '90px',
        whiteSpace: 'normal',
        verticalAlign: 'bottom',
    },
    heatRowHeader: {
        ...shorthands.padding('2px', '6px'),
        whiteSpace: 'nowrap',
        fontWeight: 600,
    },
    heatCellBtn: {
        width: '100%',
        minWidth: '40px',
        height: '28px',
        ...shorthands.borderRadius('2px'),
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
        fontVariantNumeric: 'tabular-nums',
    },
    scrollX: {
        overflowX: 'auto',
    },
    trendBars: {
        display: 'flex',
        alignItems: 'flex-end',
        ...shorthands.gap('6px'),
        height: '180px',
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
        paddingBottom: '4px',
    },
    trendCol: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexGrow: 1,
        height: '100%',
        ...shorthands.gap('4px'),
    },
    trendBar: {
        width: '60%',
        minWidth: '10px',
        backgroundColor: tokens.colorBrandBackground,
        ...shorthands.borderRadius('2px', '2px', '0', '0'),
    },
    trendLabel: {
        fontSize: '0.6rem',
        whiteSpace: 'nowrap',
        transform: 'rotate(-45deg)',
        transformOrigin: 'top left',
        marginTop: '8px',
        color: tokens.colorNeutralForeground3,
    },
});

const MS_PER_DAY = 86400000;

function toNumber(n: number): string {
    return n.toLocaleString();
}

function FailureAnalysis(): JSX.Element {
    usePageTitle('Failure Analysis');
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [startTime, setStartTime] = useState<Date>(() => {
        const d = new Date();
        d.setUTCDate(d.getUTCDate() - 30);
        return d;
    });
    const [endTime, setEndTime] = useState<Date>(() => new Date());
    const [bin, setBin] = useState<TimeBin>('Day');
    const [countries, setCountries] = useState<string[]>([]);
    const [suppliers, setSuppliers] = useState<string[]>([]);
    const [mode, setMode] = useState<string>('default'); // default = Live

    // Applied filters (only change when "Apply" is pressed) so charts don't refetch on every keystroke.
    const [applied, setApplied] = useState({ startTime, endTime, bin, countries, suppliers, mode });

    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [rows, setRows] = useState<FailedDocumentRow[]>([]);
    const [succeededCount, setSucceededCount] = useState(0);
    const [rawFailedCount, setRawFailedCount] = useState(0);
    const [facets, setFacets] = useState<any[]>([]); // eslint-disable-line @typescript-eslint/no-explicit-any
    const [accessToken, setAccessToken] = useState('');

    async function getAccessToken(): Promise<string> {
        if (accessToken === '') {
            const token = await acquireAccessToken(instance, accounts[0]);
            setAccessToken(token);
            return token;
        }
        return accessToken;
    }

    function buildSearchParams(includeEntityFilters = true): ReturnType<typeof getDefaultSearchParams> {
        const params = getDefaultSearchParams();
        params.startTime = applied.startTime;
        params.endTime = applied.endTime;
        params.searchText = '';
        if (includeEntityFilters && applied.countries.length > 0) {
            params.filters.countryCode = applied.countries;
        }
        if (includeEntityFilters && applied.suppliers.length > 0) {
            params.filters.supplier = applied.suppliers;
        }
        if (applied.mode) {
            params.filters.mode = [applied.mode];
        }
        return params;
    }

    useEffect(() => {
        let ignore = false;

        async function load(): Promise<void> {
            setIsLoading(true);
            setIsError(false);
            try {
                const token = await getAccessToken();
                if (token === '') {
                    // An interactive sign-in/redirect is in progress; skip the
                    // call so we do not send an empty bearer token.
                    return;
                }
                const searchParams = buildSearchParams();
                const facetParams = buildSearchParams(false);

                // 1) Recovery-aware failed documents CSV (backend already excludes recovered docs).
                const failedUrl = constructExportFailedDocumentsUrl(
                    Configuration.eventServiceUrl,
                    applied.startTime,
                    applied.endTime,
                    '',
                );
                const failedResult = await fetch(failedUrl, constructPostRequest(searchParams, token));

                // 2) Status facets (no status filter) for succeeded / raw-failed volumes.
                const searchUrl = constructDocumentSearchUrl(
                    Configuration.eventServiceUrl,
                    applied.startTime,
                    applied.endTime,
                    '*',
                );
                const searchResult = await fetch(searchUrl, constructPostRequest(searchParams, token));

                // 3) Dropdown facets scoped to mode + date only (no country/supplier),
                //    so the Country/Supplier lists show every option for the current
                //    Mode and date window and refresh on each Apply.
                const facetUrl = constructDocumentSearchUrl(
                    Configuration.eventServiceUrl,
                    applied.startTime,
                    applied.endTime,
                    '*',
                );
                const facetResult = await fetch(facetUrl, constructPostRequest(facetParams, token));

                if (ignore) {
                    return;
                }

                if (!failedResult.ok) {
                    if (failedResult.status === 401 && recoverFromUnauthorized()) {
                        // Token was missing/expired/rejected — a one-shot clean
                        // re-login was triggered (page is reloading).
                        return;
                    }
                    setIsError(true);
                    setIsLoading(false);
                    return;
                }
                clearReauthFlag();

                const csv = await failedResult.text();
                const parsed = parseFailedDocumentsCsv(csv);
                setRows(parsed);

                if (searchResult.ok) {
                    const searchJson = await searchResult.json();
                    const statusFacet = (searchJson.facets ?? []).find(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (f: any) => f.dimension === 'status',
                    );
                    const totals = statusFacet?.aggregatedTotals ?? {};
                    setSucceededCount(Number(totals['succeeded'] ?? 0));
                    setRawFailedCount(Number(totals['failed'] ?? 0));
                } else {
                    // Reset the status-derived KPIs when the facet request fails, otherwise the
                    // Total/Succeeded/Failure rate/Recovered tiles would keep stale values from a
                    // previous load while the failed rows below reflect the current filter selection.
                    setSucceededCount(0);
                    setRawFailedCount(0);
                }

                if (facetResult.ok) {
                    const facetJson = await facetResult.json();
                    setFacets(facetJson.facets ?? []);
                }

                setIsLoading(false);
            } catch {
                if (!ignore) {
                    setIsError(true);
                    setIsLoading(false);
                }
            }
        }

        load();
        return () => {
            ignore = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applied]);

    const analysis: Analysis = useMemo(() => buildAnalysis(rows, applied.bin), [rows, applied.bin]);

    const normalizedFailures = analysis.total;
    const recovered = Math.max(rawFailedCount - normalizedFailures, 0);
    const denominator = succeededCount + normalizedFailures;
    const failureRate = denominator > 0 ? (normalizedFailures / denominator) * 100 : 0;

    const dropdownOptionsFor = (dimension: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const facet = facets.find((f: any) => f.dimension === dimension);
        return facet ? getOptionsFromDimension(facet, false) : [];
    };

    const countryOptions = dropdownOptionsFor('countryCode');
    const supplierOptions = dropdownOptionsFor('supplier');

    function apply(): void {
        setApplied({ startTime, endTime, bin, countries, suppliers, mode });
    }

    function downloadRows(subset: FailedDocumentRow[], name: string): void {
        saveAs(buildDetailCsv(subset), name);
    }

    function downloadAll(): void {
        downloadRows(analysis.rows, 'failure-analysis.csv');
    }

    const maxRootCause = Math.max(1, ...analysis.byRootCause.map((r) => r.count));
    const maxCountry = Math.max(1, ...analysis.byCountry.map((r) => r.count));
    const maxBin = Math.max(1, ...analysis.byBin.map((r) => r.count));
    const maxHeat = Math.max(1, ...analysis.heatmap.map((c) => c.count));
    const maxSource = Math.max(1, ...analysis.bySource.map((r) => r.count));
    const sourceSplitByCause = new Map(analysis.rootCauseBySource.map((s) => [s.key, s]));

    const withinRecoveryWindow =
        applied.endTime.getTime() > Date.now() - 7 * MS_PER_DAY;

    return (
        <div className={styles.root}>
            <Title3 as="h1">Failure Analysis</Title3>
            <Text size={200}>
                Recovery-aware view of failed e-invoices. Documents that later succeeded (retried at any
                time up to now) are automatically excluded, matching the monthly MBR analysis.
            </Text>

            <div className={styles.filterBar}>
                <div className={styles.field}>
                    <Label htmlFor="fa-start">Start date</Label>
                    <DatePicker
                        id="fa-start"
                        value={startTime}
                        onSelectDate={(d) => d && setStartTime(d)}
                        strings={defaultDatePickerStrings}
                        allowTextInput
                    />
                </div>
                <div className={styles.field}>
                    <Label htmlFor="fa-end">End date</Label>
                    <DatePicker
                        id="fa-end"
                        value={endTime}
                        onSelectDate={(d) => d && setEndTime(d)}
                        strings={defaultDatePickerStrings}
                        allowTextInput
                    />
                </div>
                <div className={styles.field}>
                    <Label htmlFor="fa-bin">Group by</Label>
                    <Dropdown
                        id="fa-bin"
                        value={bin}
                        selectedOptions={[bin]}
                        onOptionSelect={(_, data) => setBin((data.optionValue as TimeBin) ?? 'Day')}
                    >
                        <Option value="Day">Day</Option>
                        <Option value="Week">Week</Option>
                        <Option value="Month">Month</Option>
                    </Dropdown>
                </div>
                <div className={styles.field}>
                    <Label htmlFor="fa-mode">Mode</Label>
                    <Dropdown
                        id="fa-mode"
                        value={mode === 'default' ? 'Live' : mode === 'test' ? 'Test' : mode === 'uat' ? 'UAT' : 'All'}
                        selectedOptions={[mode]}
                        onOptionSelect={(_, data) => setMode((data.optionValue as string) ?? '')}
                    >
                        <Option value="">All</Option>
                        <Option value="default">Live</Option>
                        <Option value="test">Test</Option>
                        <Option value="uat">UAT</Option>
                    </Dropdown>
                </div>
                <div className={styles.field}>
                    <Label htmlFor="fa-country">Country</Label>
                    <Dropdown
                        id="fa-country"
                        multiselect
                        placeholder="All countries"
                        value={countries.length > 0 ? countries.join(', ') : ''}
                        selectedOptions={countries}
                        onOptionSelect={(_, data) => setCountries(data.selectedOptions)}
                    >
                        {countryOptions.map((o) => (
                            <Option key={String(o.key)} value={String(o.key)}>
                                {o.text}
                            </Option>
                        ))}
                    </Dropdown>
                </div>
                <div className={styles.field}>
                    <Label htmlFor="fa-supplier">Supplier</Label>
                    <Dropdown
                        id="fa-supplier"
                        multiselect
                        placeholder="All suppliers"
                        value={suppliers.length > 0 ? suppliers.join(', ') : ''}
                        selectedOptions={suppliers}
                        onOptionSelect={(_, data) => setSuppliers(data.selectedOptions)}
                    >
                        {supplierOptions.map((o) => (
                            <Option key={String(o.key)} value={String(o.key)}>
                                {o.text}
                            </Option>
                        ))}
                    </Dropdown>
                </div>
                <div className={styles.field}>
                    <Button appearance="primary" icon={<ArrowSyncRegular />} onClick={apply}>
                        Apply
                    </Button>
                </div>
                <div className={styles.field}>
                    <Button
                        appearance="secondary"
                        icon={<ArrowDownloadRegular />}
                        onClick={downloadAll}
                        disabled={analysis.total === 0}
                    >
                        Export CSV
                    </Button>
                </div>
            </div>

            {withinRecoveryWindow && (
                <MessageBar intent="warning">
                    <MessageBarBody>
                        <MessageBarTitle>Recent window</MessageBarTitle>
                        Some failures in the last few days may still be retried and succeed, so normalized
                        counts can revise downward over time.
                    </MessageBarBody>
                </MessageBar>
            )}

            {isLoading ? (
                <Loading />
            ) : isError ? (
                <ErrorState
                    title="Couldn't load failure analysis"
                    description="The failed-documents export could not be retrieved. Check your access and try again."
                    action={{ label: 'Retry', onClick: apply }}
                />
            ) : (
                <>
                    <div className={styles.kpiRow}>
                        <div className={styles.kpiCard}>
                            <span className={styles.kpiValue}>{toNumber(denominator)}</span>
                            <span className={styles.kpiLabel}>Total invoices (attempted)</span>
                        </div>
                        <div className={styles.kpiCard}>
                            <span className={styles.kpiValue}>{toNumber(succeededCount)}</span>
                            <span className={styles.kpiLabel}>Succeeded</span>
                        </div>
                        <div className={styles.kpiCard}>
                            <span className={styles.kpiValue}>{toNumber(normalizedFailures)}</span>
                            <span className={styles.kpiLabel}>Normalized failures</span>
                        </div>
                        <div className={styles.kpiCard}>
                            <span className={styles.kpiValue}>{failureRate.toFixed(2)}%</span>
                            <span className={styles.kpiLabel}>Failure rate</span>
                        </div>
                        <div className={styles.kpiCard}>
                            <span className={styles.kpiValue}>{toNumber(recovered)}</span>
                            <span className={styles.kpiLabel}>Recovered (auto-excluded)</span>
                        </div>
                    </div>

                    <div className={styles.panels}>
                        {/* Root cause breakdown */}
                        <div className={styles.panel}>
                            <Text weight="semibold">Failures by root cause</Text>
                            {analysis.byRootCause.length === 0 ? (
                                <Text size={200}>No failures in range.</Text>
                            ) : (
                                <>
                                    <div className={styles.legend}>
                                        <span className={styles.legendItem}>
                                            <span
                                                className={styles.legendSwatch}
                                                style={{ backgroundColor: '#8764B8' }}
                                            />
                                            Provider-returned
                                        </span>
                                        <span className={styles.legendItem}>
                                            <span
                                                className={styles.legendSwatch}
                                                style={{ backgroundColor: tokens.colorBrandBackground }}
                                            />
                                            Unknown
                                        </span>
                                    </div>
                                    {analysis.byRootCause.map((item) => {
                                        const split = sourceSplitByCause.get(item.key as RootCause);
                                        const unknown = split?.unknown ?? item.count;
                                        const provider = split?.provider ?? 0;
                                        return (
                                            <div key={item.key} className={styles.barRow}>
                                                <span className={styles.barLabel} title={item.key}>
                                                    {item.key}
                                                </span>
                                                <div className={styles.barTrack}>
                                                    <div className={styles.barStack}>
                                                        <div
                                                            className={styles.barFillProvider}
                                                            style={{ width: `${(provider / maxRootCause) * 100}%` }}
                                                            title={`Provider-returned: ${provider}`}
                                                        />
                                                        <div
                                                            className={styles.barFillUnknown}
                                                            style={{ width: `${(unknown / maxRootCause) * 100}%` }}
                                                            title={`Unknown: ${unknown}`}
                                                        />
                                                    </div>
                                                </div>
                                                <span
                                                    className={styles.barCount}
                                                    title={`Provider-returned ${provider} · Unknown ${unknown}`}
                                                >
                                                    {toNumber(item.count)}
                                                </span>
                                                <Button
                                                    className={styles.downloadBtn}
                                                    appearance="subtle"
                                                    size="small"
                                                    icon={<ArrowDownloadRegular />}
                                                    aria-label={`Download ${item.key} failures`}
                                                    onClick={() =>
                                                        downloadRows(
                                                            analysis.rows.filter((r) => r.rootCause === item.key),
                                                            `failures-${item.key.replace(/[^a-z0-9]+/gi, '-')}.csv`,
                                                        )
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>

                        {/* Country breakdown */}
                        <div className={styles.panel}>
                            <Text weight="semibold">Failures by country</Text>
                            {analysis.byCountry.length === 0 ? (
                                <Text size={200}>No failures in range.</Text>
                            ) : (
                                analysis.byCountry.map((item) => (
                                    <div key={item.key} className={styles.barRow}>
                                        <span className={styles.barLabel} title={item.key}>
                                            {item.key}
                                        </span>
                                        <div className={styles.barTrack}>
                                            <div
                                                className={styles.barFill}
                                                style={{ width: `${(item.count / maxCountry) * 100}%` }}
                                            />
                                        </div>
                                        <span className={styles.barCount}>{toNumber(item.count)}</span>
                                        <Button
                                            className={styles.downloadBtn}
                                            appearance="subtle"
                                            size="small"
                                            icon={<ArrowDownloadRegular />}
                                            aria-label={`Download ${item.key} failures`}
                                            onClick={() =>
                                                downloadRows(
                                                    analysis.rows.filter((r) => r.countryCode === item.key),
                                                    `failures-${item.key.replace(/[^a-z0-9]+/gi, '-')}.csv`,
                                                )
                                            }
                                        />
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Error source breakdown: authoritative SupplierErrorCode with receipt fallback */}
                        <div className={styles.panel}>
                            <Text weight="semibold">Failures by error source</Text>
                            <Text size={100} block style={{ color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                                Provider-returned = the provider returned an error code (SupplierErrorCode),
                                or — for rows not yet reindexed — a provider receipt (ExternalReceiptId) was
                                present. Unknown = no provider signal was captured (not necessarily service-caused).
                            </Text>
                            {analysis.bySource.length === 0 ? (
                                <Text size={200}>No failures in range.</Text>
                            ) : (
                                analysis.bySource.map((item) => (
                                    <div key={item.key} className={styles.barRow}>
                                        <span className={styles.barLabel} title={item.key}>
                                            {item.key === 'Provider' ? 'Provider-returned' : 'Unknown'}
                                        </span>
                                        <div className={styles.barTrack}>
                                            <div
                                                className={styles.barFill}
                                                style={{
                                                    width: `${(item.count / maxSource) * 100}%`,
                                                    backgroundColor:
                                                        item.key === 'Provider'
                                                            ? '#8764B8'
                                                            : tokens.colorBrandBackground,
                                                }}
                                            />
                                        </div>
                                        <span className={styles.barCount}>{toNumber(item.count)}</span>
                                        <Button
                                            className={styles.downloadBtn}
                                            appearance="subtle"
                                            size="small"
                                            icon={<ArrowDownloadRegular />}
                                            aria-label={`Download ${item.key} failures`}
                                            onClick={() =>
                                                downloadRows(
                                                    analysis.rows.filter((r) => r.errorSource === item.key),
                                                    `failures-${String(item.key).toLowerCase()}.csv`,
                                                )
                                            }
                                        />
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Trend */}
                        <div className={`${styles.panel} ${styles.panelWide}`}>
                            <Text weight="semibold">Normalized failures over time ({applied.bin})</Text>
                            {analysis.byBin.length === 0 ? (
                                <Text size={200}>No failures in range.</Text>
                            ) : (
                                <div className={styles.trendBars}>
                                    {analysis.byBin.map((item) => (
                                        <div key={item.key} className={styles.trendCol} title={`${item.key}: ${item.count}`}>
                                            <span className={styles.barCount}>{item.count}</span>
                                            <div
                                                className={styles.trendBar}
                                                style={{ height: `${(item.count / maxBin) * 100}%` }}
                                            />
                                            <span className={styles.trendLabel}>{formatBinLabel(item.key, applied.bin)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Heatmap */}
                        <div className={`${styles.panel} ${styles.panelWide}`}>
                            <Text weight="semibold">Country × root cause (click a cell to download)</Text>
                            {analysis.countries.length === 0 ? (
                                <Text size={200}>No failures in range.</Text>
                            ) : (
                                <div className={styles.scrollX}>
                                    <table className={styles.heatTable}>
                                        <thead>
                                            <tr>
                                                <th className={styles.heatTh}>Country</th>
                                                {analysis.rootCauses.map((rc) => (
                                                    <th
                                                        key={rc}
                                                        className={`${styles.heatTh} ${styles.heatThRotate}`}
                                                        scope="col"
                                                    >
                                                        {rc}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {analysis.countries.map((c) => (
                                                <tr key={c}>
                                                    <th scope="row" className={styles.heatRowHeader}>
                                                        {c}
                                                    </th>
                                                    {analysis.rootCauses.map((rc) => {
                                                        const cell = analysis.heatmap.find(
                                                            (h) => h.country === c && h.rootCause === rc,
                                                        );
                                                        const count = cell?.count ?? 0;
                                                        const intensity = count / maxHeat;
                                                        return (
                                                            <td key={rc} style={{ padding: '1px' }}>
                                                                {count === 0 ? (
                                                                    <div
                                                                        className={styles.heatCellBtn}
                                                                        style={{
                                                                            backgroundColor:
                                                                                tokens.colorNeutralBackground2,
                                                                            color: tokens.colorNeutralForeground4,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                        }}
                                                                        aria-hidden="true"
                                                                    >
                                                                        0
                                                                    </div>
                                                                ) : (
                                                                    <Button
                                                                        className={styles.heatCellBtn}
                                                                        appearance="transparent"
                                                                        aria-label={`${count} ${rc} failures in ${c}, download`}
                                                                        style={{
                                                                            backgroundColor: `rgba(196, 49, 75, ${
                                                                                0.15 + intensity * 0.7
                                                                            })`,
                                                                            color:
                                                                                intensity > 0.5
                                                                                    ? '#ffffff'
                                                                                    : tokens.colorNeutralForeground1,
                                                                        }}
                                                                        onClick={() =>
                                                                            downloadRows(
                                                                                analysis.rows.filter(
                                                                                    (r) =>
                                                                                        (r.countryCode || 'Unknown') === c &&
                                                                                        r.rootCause === rc,
                                                                                ),
                                                                                `failures-${c}-${rc.replace(
                                                                                    /[^a-z0-9]+/gi,
                                                                                    '-',
                                                                                )}.csv`,
                                                                            )
                                                                        }
                                                                    >
                                                                        {count}
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default FailureAnalysis;

// Re-export for convenience.
export type { RootCause };
